import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Calendar, Clock, Globe2, CheckCircle2, XCircle, AlertTriangle, Pencil, X } from "lucide-react";
import api from "@/lib/api";
import SearchableSelect from "@/components/SearchableSelect";

const DAYS = [
  { k: "mon", label: "Mon" }, { k: "tue", label: "Tue" }, { k: "wed", label: "Wed" },
  { k: "thu", label: "Thu" }, { k: "fri", label: "Fri" }, { k: "sat", label: "Sat" },
  { k: "sun", label: "Sun" },
];

function timeUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return "due now";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h >= 24) return `in ${Math.floor(h / 24)}d ${h % 24}h`;
  if (h >= 1) return `in ${h}h ${m}m`;
  return `in ${m}m`;
}

function timeAgo(iso) {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  const h = Math.floor(m / 60);
  if (h < 1) return `${m}m ago`;
  const d = Math.floor(h / 24);
  if (d < 1) return `${h}h ago`;
  return `${d}d ago`;
}

export default function SchedulePanel({ projectId, canEdit, className = "mt-6 card-crisp p-4" }) {
  const [schedule, setSchedule] = useState(null);
  const [summary, setSummary] = useState("");
  const [role, setRole] = useState("viewer");
  const [editing, setEditing] = useState(false);
  const [tzList, setTzList] = useState({ items: [], common: [] });
  const [loadError, setLoadError] = useState(null);

  // form state
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState([]);
  const [time, setTime] = useState("09:00");
  const [tz, setTz] = useState("UTC");
  const [intervalWeeks, setIntervalWeeks] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/projects/${projectId}/schedule`).then((r) => {
    setLoadError(null);
    setSchedule(r.data.schedule || {});
    setSummary(r.data.summary || "");
    setRole(r.data.role || "viewer");
    const s = r.data.schedule || {};
    setEnabled(!!s.enabled);
    setDays(s.days || []);
    setTime(s.time || "09:00");
    setTz(s.timezone || "UTC");
    setIntervalWeeks(s.interval_weeks || 1);
  }).catch((e) => {
    setLoadError(e?.response?.data?.detail || "Could not load refresh schedule.");
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  useEffect(() => { api.get("/meta/timezones").then((r) => setTzList({ items: r.data.items.map((z) => ({ code: z.name, name: `${z.label} · ${z.offset}` })), common: r.data.common })); }, []);

  const canConfigure = canEdit && (role === "owner" || role === "admin");

  const toggleDay = (d) => setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);

  const save = async () => {
    if (enabled && days.length === 0) return toast.error("Choose at least one day.");
    setBusy(true);
    try {
      const r = await api.put(`/projects/${projectId}/schedule`, { enabled, days, time, timezone: tz, interval_weeks: intervalWeeks });
      setSchedule(r.data.schedule);
      setSummary(r.data.summary);
      toast.success(enabled ? "Schedule saved." : "Auto-refresh turned off.");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule.");
    } finally { setBusy(false); }
  };

  const setPreset = (preset) => {
    if (preset === "daily") { setDays(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]); setIntervalWeeks(1); }
    else if (preset === "weekdays") { setDays(["mon", "tue", "wed", "thu", "fri"]); setIntervalWeeks(1); }
    else if (preset === "weekend") { setDays(["sat", "sun"]); setIntervalWeeks(1); }
    else if (preset === "weekly") { setDays(["mon"]); setIntervalWeeks(1); }
    else if (preset === "biweekly") { setDays(["mon"]); setIntervalWeeks(2); }
    else if (preset === "clear") { setDays([]); setIntervalWeeks(1); }
  };

  const presetIsActive = (preset) => {
    if (!enabled) return false;
    const set = new Set(days);
    if (preset === "daily") return intervalWeeks === 1 && set.size === 7;
    if (preset === "weekdays") return intervalWeeks === 1 && set.size === 5 && ["mon","tue","wed","thu","fri"].every((d) => set.has(d));
    if (preset === "weekend") return intervalWeeks === 1 && set.size === 2 && set.has("sat") && set.has("sun");
    if (preset === "weekly") return intervalWeeks === 1 && set.size === 1 && set.has("mon");
    if (preset === "biweekly") return intervalWeeks === 2 && set.size === 1 && set.has("mon");
    return false;
  };

  const statusPill = useMemo(() => {
    if (!schedule?.enabled) return { label: "Auto-refresh off", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: XCircle };
    if (schedule?.last_status === "paused_trial_expired") return { label: "Paused — trial ended", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: AlertTriangle };
    if (schedule?.last_status === "failed") return { label: "Last run failed", cls: "bg-red-50 text-red-700 border-red-200", Icon: AlertTriangle };
    return { label: "Auto-refresh on", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 };
  }, [schedule]);

  if (loadError) {
    return (
      <div className={`${className} border-red-200 bg-red-50`} data-testid="schedule-panel-error">
        <div className="text-[11px] uppercase tracking-wider text-red-700 font-medium">Refresh schedule</div>
        <div className="mt-1 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {loadError}
        </div>
        <button onClick={load} className="mt-2 btn-ghost px-3 py-1.5 rounded-md text-xs">Retry</button>
      </div>
    );
  }
  if (!schedule) return null;

  return (
    <div className={className} data-testid="schedule-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#8A94A6] font-medium">Refresh schedule</div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border font-medium uppercase tracking-wider ${statusPill.cls}`} data-testid="schedule-status-pill">
              <statusPill.Icon className="h-3 w-3" /> {statusPill.label}
            </span>
            <span className="text-sm text-[#0A0D14] font-medium" data-testid="schedule-summary">{summary}</span>
          </div>
          <div className="mt-2 text-xs text-[#525B6A] flex flex-wrap items-center gap-4">
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-[#8A94A6]" /> Next run: <span className="tabular-nums text-[#0A0D14]" data-testid="schedule-next">{schedule.enabled ? (schedule.next_run_at ? `${timeUntil(schedule.next_run_at)} · ${new Date(schedule.next_run_at).toLocaleString()}` : "—") : "—"}</span></span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3 text-[#8A94A6]" /> Last run: <span className="text-[#0A0D14]" data-testid="schedule-last">{schedule.last_run_at ? `${timeAgo(schedule.last_run_at)} (${schedule.last_status || 'ok'})` : "never"}</span></span>
            <span className="inline-flex items-center gap-1"><Globe2 className="h-3 w-3 text-[#8A94A6]" /> <span className="font-mono text-[11px]">{schedule.timezone || 'UTC'}</span></span>
          </div>
        </div>
        {canConfigure && !editing && (
          <button onClick={() => setEditing(true)} data-testid="schedule-edit-btn" className="btn-ghost px-3 py-1.5 rounded-md text-sm inline-flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> {schedule?.enabled ? "Edit" : "Set schedule"}</button>
        )}
      </div>

      {editing && (
        <div className="mt-4 border-t border-[#E2E8F0] pt-4 space-y-4" data-testid="schedule-editor">
          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-[#0A0D14] cursor-pointer">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="schedule-enabled" className="accent-[#0047FF] h-4 w-4" />
              <span className="font-medium">Enable auto-refresh</span>
            </label>
            <button onClick={() => setEditing(false)} className="text-[#8A94A6] hover:text-[#0A0D14]" data-testid="schedule-cancel"><X className="h-4 w-4" /></button>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium block mb-2">Days</label>
            <div className="flex gap-1.5 flex-wrap" data-testid="schedule-days">
              {DAYS.map((d) => (
                <button key={d.k} type="button" disabled={!enabled} onClick={() => toggleDay(d.k)} data-testid={`schedule-day-${d.k}`} className={`px-3 py-1.5 rounded-md text-xs border font-medium ${days.includes(d.k) ? 'bg-[#0047FF] border-[#0047FF] text-white' : 'bg-white border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF] hover:text-[#0047FF]'} ${!enabled ? 'opacity-40 cursor-not-allowed' : ''}`}>{d.label}</button>
              ))}
            </div>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium mb-1.5">Presets</div>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="schedule-presets">
                {[
                  { k: "daily", label: "Daily" },
                  { k: "weekdays", label: "Weekdays" },
                  { k: "weekend", label: "Weekend" },
                  { k: "weekly", label: "Every week" },
                  { k: "biweekly", label: "Every 2 weeks" },
                  { k: "clear", label: "Clear" },
                ].map((p) => (
                  <button
                    key={p.k}
                    type="button"
                    onClick={() => setPreset(p.k)}
                    disabled={!enabled}
                    data-testid={`schedule-preset-${p.k}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] border font-medium ${presetIsActive(p.k) ? 'bg-[#0047FF] border-[#0047FF] text-white' : p.k === 'clear' ? 'bg-white border-[#E2E8F0] text-red-600 hover:border-red-500' : 'bg-white border-[#E2E8F0] text-[#0047FF] hover:border-[#0047FF]'} ${!enabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium block mb-1">Time (24h)</label>
              <div className="flex items-center gap-2" data-testid="schedule-time">
                <select disabled={!enabled} value={time.split(":")[0] || "09"} onChange={(e) => setTime(`${e.target.value}:${(time.split(":")[1] || "00")}`)} data-testid="schedule-hour" className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm font-mono bg-white">
                  {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((h) => <option key={h}>{h}</option>)}
                </select>
                <span className="text-[#8A94A6]">:</span>
                <select disabled={!enabled} value={time.split(":")[1] || "00"} onChange={(e) => setTime(`${(time.split(":")[0] || "09")}:${e.target.value}`)} data-testid="schedule-minute" className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm font-mono bg-white">
                  {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map((m) => <option key={m}>{m}</option>)}
                </select>
                <span className="ml-1 text-[10px] uppercase tracking-wider text-[#8A94A6]">24h · UTC-safe</span>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium block mb-1">Timezone</label>
              <SearchableSelect items={tzList.items} common={tzList.common} value={tz} onChange={setTz} testid="schedule-timezone" disabled={!enabled} placeholder="Pick a timezone" hideCode />
            </div>
          </div>

          <div className="rounded-md bg-[#F8F9FA] border border-[#E2E8F0] px-3 py-2 text-xs text-[#525B6A]" data-testid="schedule-explainer">
            Refresh runs within ~15 minutes of the scheduled time. Times are stored in UTC. All display uses 24-hour format.
          </div>

          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setEditing(false)} className="btn-ghost px-3 py-2 rounded-md text-sm">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="schedule-save" className="btn-primary px-4 py-2 rounded-md text-sm disabled:opacity-50">{busy ? "Saving…" : "Save schedule"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
